<template>
  <div v-redirect-if-no-permission:MANAGE_FINANCE_JOURNALS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-dialog v-model="cashDialog" max-width="720px" persistent>
      <v-card>
        <v-card-title>
          {{ $t("finance.journals.newCashMovement") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeCashDialog"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-layout wrap>
            <v-flex xs12 md4 class="pa-2">
              <v-select :items="cashDirectionOptions" item-text="text" item-value="value" v-model="cashForm.direction" :label="$t('finance.journals.direction')"></v-select>
            </v-flex>
            <v-flex xs12 md4 class="pa-2">
              <v-autocomplete :items="accountOptions" item-text="text" item-value="value" v-model="cashForm.counter_account_id" :label="$t('finance.journals.counterAccount')"></v-autocomplete>
            </v-flex>
            <v-flex xs12 md4 class="pa-2">
              <v-text-field v-model.number="cashForm.amount" :label="$t('finance.common.amount')" type="number"></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <choose-captains-multi :multi="false" :label="$t('finance.journals.captainOptional')" v-model="cashForm.captain_id" @selected="cashCaptain = $event" />
              <div v-if="cashCaptain" class="driver-account-row">
                <v-btn v-if="cashCaptain.gl_account_id" small outline color="primary" @click="useDriverAccountOnCash">
                  {{ $t("finance.journals.useDriverAccount", { code: cashCaptain.gl_account_code }) }}
                </v-btn>
                <span v-else class="driver-account-none">{{ $t("finance.journals.driverHasNoAccount") }}</span>
              </div>
            </v-flex>
            <v-flex xs12 md3 class="pa-2">
              <v-text-field v-model="cashForm.reference_no" :label="$t('finance.journals.referenceNo')"></v-text-field>
            </v-flex>
            <v-flex xs12 md3 class="pa-2">
              <v-text-field v-model="cashForm.posted_at" :label="$t('finance.journals.postingDate')" type="date"></v-text-field>
            </v-flex>
            <v-flex xs12 class="pa-2">
              <v-text-field v-model="cashForm.memo" :label="$t('finance.common.memo')"></v-text-field>
            </v-flex>
            <v-flex xs12 class="px-2" v-if="isEntityLeaf(cashForm.counter_account_id)">
              <v-chip small color="orange darken-2" text-color="white" class="entity-leaf-chip">
                <v-icon small left>warning</v-icon>{{ $t("finance.journals.entityLeafWarning") }}
              </v-chip>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeCashDialog">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="primary" :loading="cashSaving" @click="submitCashMovement">{{ $t("finance.journals.postCashMovement") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="payrollDialog" max-width="980px" persistent>
      <v-card>
        <v-card-title>
          {{ $t("finance.journals.newPayrollBatch") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closePayrollDialog"><v-icon>close</v-icon></v-btn>
        </v-card-title>

        <!-- Accountant doc 2026-10-03 item 1.10: after posting, show what
             reached the journal (entries, accounts, beneficiaries). -->
        <v-card-text v-if="payrollResult">
          <div class="payroll-result-title">{{ $t("finance.journals.payrollResultTitle") }}</div>
          <div v-for="posted in payrollResult.entries" :key="'payroll-posted-' + posted.id" class="line-row pa-2">
            <div class="entry-header">
              <strong>{{ posted.reference_no || ('JE-' + posted.id) }}</strong>
              <!-- Final-version doc 2026-10-05 ("one hour difference"): the
                   entry date stays the main value; the recording time shows
                   under it when the two differ. -->
              <span class="entry-date">
                <span>{{ formatDate(posted.journal_date) }}</span>
                <span v-if="showRecordedAt(posted)" class="entry-recorded" :title="$t('finance.journals.recordedAtHint')">
                  {{ $t("finance.journals.recordedAt") }}: <span class="entry-recorded-value">{{ formatDate(posted.created_at) }}</span>
                </span>
              </span>
              <span>{{ posted.memo }}</span>
            </div>
            <v-data-table class="fin-dt" :headers="lineHeaders" :items="posted.lines || []" hide-actions>
              <template v-slot:items="props">
                <tr>
                  <td class="fin-code">{{ props.item.account ? props.item.account.code : '-' }}</td>
                  <td>{{ props.item.account ? localizedAccountName(props.item.account) : '-' }}</td>
                  <td>{{ formatEntryType(props.item.entry_type) }}</td>
                  <td class="fin-num">{{ formatAmount(props.item.amount) }}</td>
                  <td>{{ props.item.memo || '-' }}</td>
                </tr>
              </template>
            </v-data-table>
          </div>
        </v-card-text>

        <v-card-text v-else>
          <v-layout wrap>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="payrollForm.memo" :label="$t('finance.common.memo')"></v-text-field>
            </v-flex>
            <v-flex xs12 md3 class="pa-2">
              <v-text-field v-model="payrollForm.reference_no" :label="$t('finance.journals.referenceNo')"></v-text-field>
            </v-flex>
            <v-flex xs12 md3 class="pa-2">
              <v-text-field v-model="payrollForm.paid_at" :label="$t('finance.journals.paidAt')" type="date"></v-text-field>
            </v-flex>
            <v-flex xs12 md4 class="pa-2">
              <v-select
                :items="payrollModeOptions"
                item-text="text"
                item-value="value"
                v-model="payrollForm.mode"
                :label="$t('finance.journals.payrollMode')"
                :hint="payrollForm.mode === 'accrued' ? $t('finance.journals.payrollModeAccruedHint') : $t('finance.journals.payrollModePaidHint')"
                persistent-hint
              ></v-select>
            </v-flex>
            <v-flex xs12 md8 class="pa-2">
              <v-autocomplete
                :items="payrollCreditAccountOptions"
                item-text="text"
                item-value="value"
                v-model="payrollForm.credit_gl_account_id"
                :label="$t('finance.journals.payrollCreditAccount')"
                :hint="$t('finance.journals.payrollCreditAccountHint')"
                persistent-hint
                clearable
              ></v-autocomplete>
            </v-flex>
          </v-layout>

          <!-- A stable per-line key: with the index, removing line 1 made
               Vue reuse line 1's driver picker for line 2's data. -->
          <div v-for="(line, index) in payrollForm.lines" :key="line.uid" class="line-row">
            <v-layout wrap align-center>
              <v-flex xs12 md3 class="pa-2">
                <v-select
                  :items="payrollBeneficiaryOptions"
                  item-text="text"
                  item-value="value"
                  v-model="line.kind"
                  :label="$t('finance.journals.beneficiaryType')"
                  @change="onPayrollKindChange(line)"
                ></v-select>
              </v-flex>
              <v-flex xs12 md5 class="pa-2">
                <v-autocomplete
                  v-if="line.kind === 'employee'"
                  :items="employeeOptions"
                  item-text="text"
                  item-value="value"
                  v-model="line.employee_id"
                  :label="$t('finance.journals.employee')"
                  :loading="employeesLoading"
                  :no-data-text="$t('finance.journals.noEmployees')"
                  @change="onPayrollEmployeeChange(line)"
                ></v-autocomplete>
                <choose-captains-multi
                  v-else-if="line.kind === 'driver'"
                  :multi="false"
                  :label="$t('finance.journals.driver')"
                  v-model="line.captain_id"
                  @selected="onPayrollDriverSelected(line, $event)"
                />
                <span v-else class="driver-account-none">{{ $t("finance.journals.freeTextBeneficiaryHint") }}</span>
              </v-flex>
              <v-flex xs12 md4 class="pa-2">
                <v-text-field v-model="line.recipient_name" :label="$t('finance.journals.recipient')"></v-text-field>
              </v-flex>
              <v-flex xs12 md3 class="pa-2">
                <v-text-field v-model.number="line.amount" :label="$t('finance.common.amount')" type="number"></v-text-field>
              </v-flex>
              <v-flex xs12 md8 class="pa-2">
                <v-text-field v-model="line.memo" :label="$t('finance.journals.lineMemo')"></v-text-field>
              </v-flex>
              <v-flex xs12 md1 class="pa-2 text-xs-center">
                <v-btn icon :disabled="payrollForm.lines.length <= 1" @click="removePayrollLine(index)">
                  <v-icon color="red">delete</v-icon>
                </v-btn>
              </v-flex>
              <v-flex xs12 class="px-2 pb-2" v-if="line.kind === 'driver' && line.driver">
                <span class="driver-account-none">
                  {{ line.driver.gl_account_code ? $t("finance.journals.payrollDriverAccount", { code: line.driver.gl_account_code }) : $t("finance.journals.payrollDriverAccountNew") }}
                </span>
              </v-flex>
            </v-layout>
          </div>

          <v-btn flat color="primary" @click="addPayrollLine">{{ $t("finance.journals.addPayrollLine") }}</v-btn>
          <div class="mt-3 summary-row">{{ $t("finance.journals.total") }}: {{ formatTotal(totalPayrollAmount) }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <template v-if="payrollResult">
            <v-btn flat @click="openPayrollDialog">{{ $t("finance.journals.newPayrollBatch") }}</v-btn>
            <v-btn color="primary" @click="closePayrollDialog">{{ $t("finance.journals.closeDialog") }}</v-btn>
          </template>
          <template v-else>
            <v-btn flat @click="closePayrollDialog">{{ $t("finance.common.cancel") }}</v-btn>
            <v-btn color="primary" :loading="payrollSaving" @click="submitPayrollBatch">{{ $t("finance.journals.postPayrollBatch") }}</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" max-width="900px" persistent>
      <v-card>
        <v-card-title>
          {{ $t("finance.journals.newJournalEntry") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDialog"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-layout wrap>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="form.memo" :label="$t('finance.common.memo')"></v-text-field>
            </v-flex>
            <v-flex xs12 md3 class="pa-2">
              <v-text-field v-model="form.reference_no" :label="$t('finance.journals.referenceNo')"></v-text-field>
            </v-flex>
            <v-flex xs12 md3 class="pa-2">
              <v-text-field v-model="form.journal_date" :label="$t('finance.journals.journalDate')" type="date"></v-text-field>
            </v-flex>
            <v-flex xs12 class="pa-2">
              <choose-captains-multi
                :multi="false"
                :label="$t('finance.journals.captainOptional')"
                v-model="form.captain_id"
                @selected="entryCaptain = $event"
              />
              <!-- Accountant doc 2026-10-03 item 1.1: one click puts the
                   chosen driver's own sub-account on a line. -->
              <div v-if="entryCaptain" class="driver-account-row">
                <v-btn v-if="entryCaptain.gl_account_id" small outline color="primary" @click="useDriverAccountOnEntry">
                  {{ $t("finance.journals.useDriverAccount", { code: entryCaptain.gl_account_code }) }}
                </v-btn>
                <span v-else class="driver-account-none">{{ $t("finance.journals.driverHasNoAccount") }}</span>
              </div>
            </v-flex>
          </v-layout>

          <v-divider class="my-3"></v-divider>

          <div
            v-for="(line, index) in form.lines"
            :key="'line-' + index"
            class="line-row"
          >
            <v-layout wrap align-center>
              <v-flex xs12 md4 class="pa-2">
                <v-autocomplete
                  :items="accountOptions"
                  item-text="text"
                  item-value="value"
                  v-model="line.gl_account_id"
                  :label="$t('finance.common.account')"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs12 md2 class="pa-2">
                <v-select
                  :items="entryTypeOptions"
                  item-text="text"
                  item-value="value"
                  v-model="line.entry_type"
                  :label="$t('finance.common.type')"
                ></v-select>
              </v-flex>
              <v-flex xs12 md2 class="pa-2">
                <v-text-field v-model.number="line.amount" :label="$t('finance.common.amount')" type="number"></v-text-field>
              </v-flex>
              <v-flex xs12 md3 class="pa-2">
                <v-text-field v-model="line.memo" :label="$t('finance.journals.lineMemo')"></v-text-field>
              </v-flex>
              <v-flex xs12 md1 class="pa-2 text-xs-center">
                <v-btn icon :disabled="form.lines.length <= 2" @click="removeLine(index)">
                  <v-icon color="red">delete</v-icon>
                </v-btn>
              </v-flex>
              <v-flex xs12 class="px-2 pb-2" v-if="isEntityLeaf(line.gl_account_id)">
                <v-chip small color="orange darken-2" text-color="white" class="entity-leaf-chip">
                  <v-icon small left>warning</v-icon>{{ $t("finance.journals.entityLeafWarning") }}
                </v-chip>
              </v-flex>
            </v-layout>
          </div>

          <v-btn flat color="primary" @click="addLine">{{ $t("finance.journals.addLine") }}</v-btn>

          <div class="mt-3 summary-row">
            {{ $t("finance.common.debit") }}: {{ formatTotal(totalDebit) }}
            <span class="mx-3">{{ $t("finance.common.credit") }}: {{ formatTotal(totalCredit) }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeDialog">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitJournalEntry">{{ $t("finance.journals.postEntry") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="reverseDialog" max-width="640px" persistent>
      <v-card>
        <v-card-title>
          {{ $t("finance.journals.reverseEntryTitle") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeReverseDialog"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="mb-3" v-if="reversalForm.target_reference_no">
            <strong>{{ reversalForm.target_reference_no }}</strong>
          </div>
          <v-layout wrap>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="reversalForm.reference_no" :label="$t('finance.journals.referenceNo')"></v-text-field>
            </v-flex>
            <v-flex xs12 md6 class="pa-2">
              <v-text-field v-model="reversalForm.journal_date" :label="$t('finance.journals.journalDate')" type="date"></v-text-field>
            </v-flex>
            <v-flex xs12 class="pa-2">
              <v-text-field v-model="reversalForm.reason" :label="$t('finance.common.reason')" :hint="$t('finance.journals.reverseReasonHint')" persistent-hint></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeReverseDialog">{{ $t("finance.common.cancel") }}</v-btn>
          <v-btn color="error" :loading="reverseSaving" @click="submitReversal">{{ $t("finance.journals.reverseEntryConfirm") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card class="mb-2">
      <div class="fin-header">
        <div class="fin-toolbar">
          <span class="fin-title">{{ $t("finance.journals.pageTitle") }}</span>
          <v-spacer></v-spacer>
          <v-btn small flat @click="openCashDialog">{{ $t("finance.journals.newCashMovement") }}</v-btn>
          <v-btn small flat @click="openPayrollDialog">{{ $t("finance.journals.newPayrollBatch") }}</v-btn>
          <v-btn small color="primary" @click="openDialog">{{ $t("finance.journals.newJournalEntry") }}</v-btn>
        </div>
        <div class="fin-filters">
          <v-text-field
            class="fin-field"
            v-model="filters.from"
            :label="$t('finance.common.from')"
            type="date"
            hide-details
          ></v-text-field>
          <v-text-field
            class="fin-field"
            v-model="filters.to"
            :label="$t('finance.common.to')"
            type="date"
            hide-details
          ></v-text-field>
          <v-select
            class="fin-field"
            :items="sourceOptions"
            item-text="text"
            item-value="value"
            v-model="filters.source"
            :label="$t('finance.common.source')"
            clearable
            hide-details
          ></v-select>
          <v-text-field
            class="fin-field"
            v-model="filters.operation_ref"
            :label="$t('finance.journals.operationRef')"
            clearable
            hide-details
          ></v-text-field>
          <v-text-field
            class="fin-field"
            v-model="filters.booking_id"
            :label="$t('finance.journals.bookingId')"
            type="number"
            clearable
            hide-details
          ></v-text-field>
          <v-text-field
            class="fin-field"
            v-model="filters.reference_no"
            :label="$t('finance.journals.referenceNo')"
            clearable
            hide-details
          ></v-text-field>
          <!-- Accountant doc 2026-10-03 item 1.10: reference, memo or any line
               memo (a payroll line memo carries the recipient's name). -->
          <v-text-field
            class="fin-field"
            v-model="filters.search"
            :label="$t('finance.journals.searchText')"
            clearable
            hide-details
            @keyup.enter="applyFilters"
          ></v-text-field>
          <v-btn small color="primary" @click="applyFilters">{{ $t("finance.common.apply") }}</v-btn>
          <v-btn small flat @click="resetFilters">{{ $t("finance.journals.reset") }}</v-btn>
        </div>
      </div>
    </v-card>

    <v-expansion-panel>
      <v-expansion-panel-content
        v-for="entry in entries"
        :key="entry.id"
      >
        <template v-slot:header>
          <div class="entry-row">
            <div class="entry-header">
              <strong>{{ entry.reference_no || ('JE-' + entry.id) }}</strong>
              <!-- Final-version doc 2026-10-05 ("one hour difference"): a ride
                   entry is dated when the ride finished but recorded when it
                   was paid; both are shown so the gap never reads as a
                   time-zone error. -->
              <span class="entry-date">
                <span>{{ formatDate(entry.journal_date) }}</span>
                <span v-if="showRecordedAt(entry)" class="entry-recorded" :title="$t('finance.journals.recordedAtHint')">
                  {{ $t("finance.journals.recordedAt") }}: <span class="entry-recorded-value">{{ formatDate(entry.created_at) }}</span>
                </span>
              </span>
              <span>{{ formatSource(entry.source) }}</span>
              <span v-if="entry.booking_id">
                <router-link :to="`/bookings/${entry.booking_id}`" @click.native.stop>
                  {{ $t('finance.journals.bookingId') }}: #{{ entry.booking_id }}
                </router-link>
              </span>
              <span>{{ entry.memo || $t('finance.journals.noMemo') }}</span>
              <!-- Accounting doc 2026-09-07 (item 4.1): who created the entry;
                   one the engine posted has no person behind it. -->
              <span class="entry-doer">{{ $t('finance.common.performedBy') }}: {{ performerName(entry) }}</span>
            </div>
            <div class="entry-actions" @click.stop>
              <v-chip small :color="entry.is_reversed ? 'error' : entry.is_reversal ? 'info' : 'success'" text-color="white">
                {{ formatEntryStatus(entry) }}
              </v-chip>
              <v-btn v-if="entry.can_reverse && hasPermission('REVERSE_FINANCE_JOURNALS')" small color="error" outline @click.stop="openReverseDialog(entry)">
                {{ $t("finance.journals.reverseEntry") }}
              </v-btn>
            </div>
          </div>
        </template>
        <v-card>
          <v-card-text v-if="entry.is_reversed || entry.is_reversal" class="pb-0">
            <div v-if="entry.is_reversed && entry.reversed_by_entry" class="entry-meta error--text">
              {{ $t("finance.journals.reversedBy") }}: {{ entry.reversed_by_entry.reference_no || ('JE-' + entry.reversed_by_entry.id) }}
            </div>
            <div v-if="entry.is_reversal && entry.reverse_of_entry" class="entry-meta info--text">
              {{ $t("finance.journals.reversalOf") }}: {{ entry.reverse_of_entry.reference_no || ('JE-' + entry.reverse_of_entry.id) }}
            </div>
          </v-card-text>
          <v-data-table
            class="fin-dt"
            :headers="lineHeaders"
            :items="entry.lines || []"
            hide-actions
          >
            <template v-slot:items="props">
              <tr>
                <td class="fin-code">{{ props.item.account ? props.item.account.code : '-' }}</td>
                <td>{{ props.item.account ? localizedAccountName(props.item.account) : '-' }}</td>
                <td>{{ props.item.entry_type }}</td>
                <td class="fin-num">{{ formatAmount(props.item.amount) }}</td>
                <td>{{ props.item.memo || '-' }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <div class="journal-paging">
      <span class="journal-paging-total">{{ $t("finance.journals.entriesTotal", { count: pagination.total }) }}</span>
      <v-pagination
        v-if="pagination.lastPage > 1"
        v-model="pagination.page"
        :length="pagination.lastPage"
        :total-visible="7"
        @input="loadEntries"
      ></v-pagination>
      <v-select
        class="journal-paging-size"
        :items="perPageOptions"
        v-model="pagination.perPage"
        :label="$t('finance.journals.perPage')"
        hide-details
        @change="applyFilters"
      ></v-select>
    </div>
  </div>
</template>

<script>
import { formatFinanceDate } from "@/mixins/financeDate";
import { toCompanyMoment } from "@/util/companyTime";
import ChooseCaptainsMulti from "@/components/monaco/input/ChooseCaptainMulti";

// Source of the payroll lines' stable render keys.
var payrollLineSeq = 0;
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";

export default {
  name: "JournalEntries",
  title() {
    return this.$t("finance.journals.pageTitle");
  },
  components: { ChooseCaptainsMulti, FinanceSectionNav, LoadingComponent },
  data() {
    return {
      loading: false,
      saving: false,
      cashSaving: false,
      payrollSaving: false,
      reverseSaving: false,
      dialog: false,
      cashDialog: false,
      payrollDialog: false,
      reverseDialog: false,
      accounts: [],
      entries: [],
      // Server-side paging of the journal list (accountant doc 2026-10-03
      // item 1.10: a 100-row window hid entries on busy days).
      pagination: { page: 1, perPage: 25, total: 0, lastPage: 1 },
      perPageOptions: [25, 50, 100],
      employees: [],
      employeesLoading: false,
      employeesLoaded: false,
      // The posted entries of the last payroll batch (success state).
      payrollResult: null,
      // Full rows of the drivers picked in the dialogs (gl_account_id /
      // gl_account_code come from captain/search); never posted.
      entryCaptain: null,
      cashCaptain: null,
      filters: {
        from: null,
        to: null,
        source: null,
        operation_ref: null,
        booking_id: null,
        reference_no: null,
        search: null,
      },
      form: this.emptyForm(),
      cashForm: this.emptyCashForm(),
      payrollForm: this.emptyPayrollForm(),
      reversalForm: this.emptyReversalForm(),
    };
  },
  computed: {
    lineHeaders() {
      return [
        { text: this.$t("finance.common.code"), value: "account.code" },
        { text: this.$t("finance.common.account"), value: "account.name" },
        { text: this.$t("finance.common.type"), value: "entry_type" },
        { text: this.$t("finance.common.amount"), value: "amount", align: "end" },
        { text: this.$t("finance.common.memo"), value: "memo" },
      ];
    },
    entryTypeOptions() {
      return [
        { text: this.$t("finance.common.debit"), value: "debit" },
        { text: this.$t("finance.common.credit"), value: "credit" },
      ];
    },
    sourceOptions() {
      return [
        { text: this.$t("finance.common.all"), value: null },
        { text: this.$t("finance.journals.manualJournals"), value: "manual_journal" },
        { text: this.$t("finance.journals.bookingRevenue"), value: "captain_booking_revenue" },
        { text: this.$t("finance.journals.settlementPaid"), value: "captain_settlement_paid" },
        { text: this.$t("finance.journals.settlementReceived"), value: "captain_settlement_received" },
        { text: this.$t("finance.journals.bonusAdjustments"), value: "captain_bonus_adjustment" },
        { text: this.$t("finance.journals.penaltyAdjustments"), value: "captain_penalty_adjustment" },
        { text: this.$t("finance.journals.hrDeductions"), value: "captain_hr_deduction_adjustment" },
        { text: this.$t("finance.journals.cashMovements"), value: "cash_movement" },
        { text: this.$t("finance.journals.payrollBatches"), value: "payroll_batch" },
        { text: this.$t("finance.journals.assetEntries"), value: "asset_entry" },
      ];
    },
    cashDirectionOptions() {
      return [
        { text: this.$t("finance.journals.cashIn"), value: "cash_in" },
        { text: this.$t("finance.journals.cashOut"), value: "cash_out" },
      ];
    },
    accountOptions() {
      // Policy doc 2026-08-23 item 1: only ACTIVE LEAF accounts are pickable.
      // Parents were the silent-slippage trap — typing a leaf code that does
      // not exist left the parent as the first substring match, and the entry
      // landed on the aggregate row. With parents filtered out, a nonexistent
      // code simply matches nothing and the form refuses to submit (the
      // backend now rejects parents/inactive/closing server-side too).
      return this.accounts
        .filter(function (account) {
          return (
            account.allows_manual_posting !== false &&
            account.is_active !== false &&
            account.has_children !== true &&
            account.kind !== "closing"
          );
        })
        .map(function (account) {
          return {
            text: this.accountOptionLabel(account),
            value: account.id,
          };
        }, this);
    },
    totalDebit() {
      return this.form.lines
        .filter(function (line) {
          return line.entry_type === "debit";
        })
        .reduce(function (sum, line) {
          return sum + Number(line.amount || 0);
        }, 0);
    },
    totalCredit() {
      return this.form.lines
        .filter(function (line) {
          return line.entry_type === "credit";
        })
        .reduce(function (sum, line) {
          return sum + Number(line.amount || 0);
        }, 0);
    },
    payrollModeOptions() {
      return [
        { text: this.$t("finance.journals.payrollModePaid"), value: "paid" },
        { text: this.$t("finance.journals.payrollModeAccrued"), value: "accrued" },
      ];
    },
    payrollBeneficiaryOptions() {
      return [
        { text: this.$t("finance.journals.employee"), value: "employee" },
        { text: this.$t("finance.journals.driver"), value: "driver" },
        { text: this.$t("finance.journals.freeTextBeneficiary"), value: "text" },
      ];
    },
    employeeOptions() {
      return this.employees.map(function (employee) {
        var label = employee.full_name;
        if (employee.employee_no) {
          label += " (" + employee.employee_no + ")";
        }
        if (employee.job_title) {
          label += " - " + employee.job_title;
        }

        return { text: label, value: employee.id };
      });
    },
    // The paying account: a postable leaf under the cash boxes (1211) or the
    // banks (1212). The backend enforces the same rule.
    payrollCreditAccountOptions() {
      var byId = {};
      this.accounts.forEach(function (account) {
        byId[account.id] = account;
      });
      var underCashOrBank = function (account) {
        var cursor = account;
        for (var depth = 0; cursor && depth < 12; depth++) {
          if (cursor.code === "1211" || cursor.code === "1212") {
            return true;
          }
          cursor = cursor.parent_id ? byId[cursor.parent_id] : null;
        }

        return false;
      };

      return this.accounts
        .filter(function (account) {
          return (
            account.allows_manual_posting !== false &&
            account.is_active !== false &&
            account.has_children !== true &&
            account.kind !== "closing" &&
            underCashOrBank(account)
          );
        })
        .map(function (account) {
          return { text: this.accountOptionLabel(account), value: account.id };
        }, this);
    },
    totalPayrollAmount() {
      return this.payrollForm.lines.reduce(function (sum, line) {
        return sum + Number(line.amount || 0);
      }, 0);
    },
  },
  mounted() {
    this.loadAccounts();
    this.loadEntries();
  },
  methods: {
    emptyForm() {
      return {
        captain_id: null,
        memo: null,
        reference_no: null,
        journal_date: null,
        lines: [
          { gl_account_id: null, entry_type: "debit", amount: null, memo: null },
          { gl_account_id: null, entry_type: "credit", amount: null, memo: null },
        ],
      };
    },
    emptyCashForm() {
      return {
        direction: "cash_out",
        counter_account_id: null,
        captain_id: null,
        amount: null,
        reference_no: null,
        posted_at: null,
        memo: null,
      };
    },
    emptyPayrollForm() {
      return {
        reference_no: null,
        paid_at: null,
        memo: null,
        mode: "paid",
        credit_gl_account_id: null,
        lines: [this.emptyPayrollLine()],
      };
    },
    emptyPayrollLine() {
      payrollLineSeq += 1;

      return {
        // Render key only (never posted): unique for the page's lifetime.
        uid: "payroll-line-" + payrollLineSeq,
        kind: "employee",
        recipient_name: null,
        captain_id: null,
        employee_id: null,
        // The captain/search row of a driver line (for its account code);
        // never posted.
        driver: null,
        amount: null,
        memo: null,
      };
    },
    emptyReversalForm() {
      return {
        id: null,
        target_reference_no: null,
        reference_no: null,
        journal_date: null,
        reason: null,
      };
    },
    getErrorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },
    localizedAccountName(account) {
      if (!account) {
        return "-";
      }

      if (this.$i18n && this.$i18n.locale === "ar" && account.name_ar) {
        return account.name_ar;
      }

      return account.name || account.name_ar || "-";
    },
    accountOptionLabel(account) {
      return account.code + " - " + this.localizedAccountName(account);
    },
    loadAccounts() {
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_CHART_OF_ACCOUNTS)
        .then(
          function (response) {
            this.accounts = response.data.data || [];
          }.bind(this)
        )
        .catch(
          function (error) {
            // Accountant doc 2026-10-03 item 1.1: an empty account picker
            // (e.g. a 403) must say why instead of looking like "no accounts".
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.journals.failedLoadAccounts")),
              "red"
            );
          }.bind(this)
        );
    },
    applyFilters() {
      this.pagination.page = 1;
      this.loadEntries();
    },
    loadEntries() {
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_JOURNAL_ENTRIES, {
          params: {
            from: this.filters.from,
            to: this.filters.to,
            source: this.filters.source,
            operation_ref: this.filters.operation_ref || undefined,
            booking_id: this.filters.booking_id || undefined,
            reference_no: this.filters.reference_no || undefined,
            search: this.filters.search || undefined,
            page: this.pagination.page,
            per_page: this.pagination.perPage,
          },
        })
        .then(
          function (response) {
            var paginator = (response.data && response.data.data) || {};
            this.entries = paginator.data || [];
            this.pagination.total = Number(paginator.total || 0);
            this.pagination.lastPage = Math.max(1, Number(paginator.last_page || 1));
            if (paginator.current_page) {
              this.pagination.page = Number(paginator.current_page);
            }
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.journals.failedLoad")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.loading = false;
          }.bind(this)
        );
    },
    resetFilters() {
      this.filters = {
        from: null,
        to: null,
        source: null,
        operation_ref: null,
        booking_id: null,
        reference_no: null,
        search: null,
      };
      this.applyFilters();
    },
    openDialog() {
      this.form = this.emptyForm();
      this.entryCaptain = null;
      this.dialog = true;
    },
    openCashDialog() {
      this.cashForm = this.emptyCashForm();
      this.cashCaptain = null;
      this.cashDialog = true;
    },
    closeCashDialog() {
      this.cashDialog = false;
      this.cashForm = this.emptyCashForm();
      this.cashCaptain = null;
    },
    openPayrollDialog() {
      this.payrollForm = this.emptyPayrollForm();
      this.payrollResult = null;
      this.payrollDialog = true;
      this.loadEmployees();
    },
    closePayrollDialog() {
      this.payrollDialog = false;
      this.payrollForm = this.emptyPayrollForm();
      this.payrollResult = null;
    },
    loadEmployees() {
      if (this.employeesLoaded || this.employeesLoading) {
        return;
      }

      this.employeesLoading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_EMPLOYEES, { params: { is_active: 1, perPage: 300 } })
        .then(
          function (response) {
            var page = (response.data && response.data.data) || {};
            this.employees = page.data || [];
            this.employeesLoaded = true;
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.journals.failedLoadEmployees")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.employeesLoading = false;
          }.bind(this)
        );
    },
    onPayrollKindChange(line) {
      line.captain_id = null;
      line.employee_id = null;
      line.driver = null;
      line.recipient_name = null;
    },
    onPayrollEmployeeChange(line) {
      var employee = null;
      for (var i = 0; i < this.employees.length; i++) {
        if (this.employees[i].id === line.employee_id) {
          employee = this.employees[i];
          break;
        }
      }
      line.recipient_name = employee ? employee.full_name : null;
    },
    onPayrollDriverSelected(line, captain) {
      var row = Array.isArray(captain) ? captain[0] : captain;
      line.driver = row || null;
      line.recipient_name = row ? [row.first_name, row.last_name].filter(Boolean).join(" ") : null;
    },
    closeDialog() {
      this.dialog = false;
      this.form = this.emptyForm();
      this.entryCaptain = null;
    },
    accountById(id) {
      if (!id) {
        return null;
      }

      for (var i = 0; i < this.accounts.length; i++) {
        if (this.accounts[i].id === id) {
          return this.accounts[i];
        }
      }

      return null;
    },
    // A driver / center / business / owner sub-account: a manual line there
    // moves the general ledger only, never the wallet in the app.
    isEntityLeaf(id) {
      var account = this.accountById(id);

      return !!(account && account.is_entity_leaf);
    },
    useDriverAccountOnEntry() {
      var accountId = this.entryCaptain && this.entryCaptain.gl_account_id;
      if (!accountId) {
        return;
      }

      var lines = this.form.lines;
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].gl_account_id === accountId) {
          return;
        }
      }

      for (var j = 0; j < lines.length; j++) {
        if (!lines[j].gl_account_id) {
          lines[j].gl_account_id = accountId;
          return;
        }
      }

      lines.push({ gl_account_id: accountId, entry_type: "debit", amount: null, memo: null });
    },
    useDriverAccountOnCash() {
      var accountId = this.cashCaptain && this.cashCaptain.gl_account_id;
      if (accountId) {
        this.cashForm.counter_account_id = accountId;
      }
    },
    openReverseDialog(entry) {
      this.reversalForm = {
        id: entry.id,
        target_reference_no: entry.reference_no || ("JE-" + entry.id),
        reference_no: null,
        journal_date: null,
        reason: null,
      };
      this.reverseDialog = true;
    },
    closeReverseDialog() {
      this.reverseDialog = false;
      this.reversalForm = this.emptyReversalForm();
    },
    addLine() {
      this.form.lines.push({
        gl_account_id: null,
        entry_type: "debit",
        amount: null,
        memo: null,
      });
    },
    removeLine(index) {
      this.form.lines.splice(index, 1);
    },
    addPayrollLine() {
      this.payrollForm.lines.push(this.emptyPayrollLine());
    },
    removePayrollLine(index) {
      this.payrollForm.lines.splice(index, 1);
    },
    submitJournalEntry() {
      if (Number(this.totalDebit) !== Number(this.totalCredit)) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.journals.debitCreditMustMatch"), "red");
        return;
      }

      this.saving = true;
      var journalPayload = Object.assign({}, this.form, {
        lines: this.form.lines.map(
          function (line) {
            return Object.assign({}, line, {
              amount: this.toStoredAmount(line.amount),
            });
          }.bind(this)
        ),
      });
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_JOURNAL_ENTRIES, journalPayload)
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.journals.journalPosted"), "green");
            this.closeDialog();
            this.loadEntries();
            this.loadAccounts();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.journals.failedPostJournal")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.saving = false;
          }.bind(this)
        );
    },
    submitCashMovement() {
      if (!this.cashForm.counter_account_id || !this.cashForm.amount) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.journals.accountAmountRequired"), "red");
        return;
      }

      this.cashSaving = true;
      var cashPayload = Object.assign({}, this.cashForm, {
        amount: this.toStoredAmount(this.cashForm.amount),
      });
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_CASH_MOVEMENTS, cashPayload)
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.journals.cashMovementPosted"), "green");
            this.closeCashDialog();
            this.loadEntries();
            this.loadAccounts();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.journals.failedCashMovement")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.cashSaving = false;
          }.bind(this)
        );
    },
    submitPayrollBatch() {
      if (!this.totalPayrollAmount) {
        window.getApp.$emit("SHOW_TOAST", this.$t("finance.journals.payrollGreaterThanZero"), "red");
        return;
      }

      for (var i = 0; i < this.payrollForm.lines.length; i++) {
        var check = this.payrollForm.lines[i];
        var named =
          (check.kind === "employee" && check.employee_id) ||
          (check.kind === "driver" && check.captain_id) ||
          (check.recipient_name && String(check.recipient_name).trim() !== "");
        if (!named) {
          window.getApp.$emit("SHOW_TOAST", this.$t("finance.journals.payrollRecipientRequired"), "red");
          return;
        }
      }

      this.payrollSaving = true;
      var payrollPayload = {
        reference_no: this.payrollForm.reference_no || null,
        paid_at: this.payrollForm.paid_at || null,
        memo: this.payrollForm.memo || null,
        mode: this.payrollForm.mode || "paid",
        credit_gl_account_id: this.payrollForm.credit_gl_account_id || null,
        lines: this.payrollForm.lines.map(
          function (line) {
            return {
              recipient_name: line.recipient_name || null,
              captain_id: line.kind === "driver" ? line.captain_id || null : null,
              employee_id: line.kind === "employee" ? line.employee_id || null : null,
              amount: this.toStoredAmount(line.amount),
              memo: line.memo || null,
            };
          }.bind(this)
        ),
      };
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_PAYROLL_BATCHES, payrollPayload)
        .then(
          function (response) {
            var data = (response.data && response.data.data) || {};
            var posted = data.journal_entries || (data.journal_entry ? [data.journal_entry] : []);
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.journals.payrollPosted"), "green");
            this.payrollResult = { entries: posted };
            this.payrollForm = this.emptyPayrollForm();
            this.applyFilters();
            this.loadAccounts();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.journals.failedPayroll")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.payrollSaving = false;
          }.bind(this)
        );
    },
    submitReversal() {
      if (!this.reversalForm.id) {
        return;
      }

      this.reverseSaving = true;
      this.axios
        .post(this.api.ACTIONS.ACCOUNTING_REVERSE_JOURNAL_ENTRY(this.reversalForm.id), {
          reference_no: this.reversalForm.reference_no,
          journal_date: this.reversalForm.journal_date,
          reason: this.reversalForm.reason,
        })
        .then(
          function () {
            window.getApp.$emit("SHOW_TOAST", this.$t("finance.journals.reversePosted"), "green");
            this.closeReverseDialog();
            this.loadEntries();
            this.loadAccounts();
          }.bind(this)
        )
        .catch(
          function (error) {
            window.getApp.$emit(
              "SHOW_TOAST",
              this.getErrorMessage(error, this.$t("finance.journals.failedReverse")),
              "red"
            );
          }.bind(this)
        )
        .finally(
          function () {
            this.reverseSaving = false;
          }.bind(this)
        );
    },
    formatAmount(value) {
      return this.formatMoney(value);
    },
    formatEntryType(value) {
      if (value === "debit") {
        return this.$t("finance.common.debit");
      }
      if (value === "credit") {
        return this.$t("finance.common.credit");
      }

      return value;
    },
    // For running totals computed by SUMMING the new-unit form inputs: they are
    // already in the new unit, so format with 2 decimals WITHOUT dividing by 100
    // (using formatAmount/formatMoney here would show them 100x too small).
    formatTotal(value) {
      return new Intl.NumberFormat("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value || 0));
    },
    formatDate(value) {
      // One shared finance formatter (accountant doc 2026-10-03 item 1.8).
      return formatFinanceDate(value);
    },
    // Final-version doc 2026-10-05 ("one hour difference"): journal_date is
    // the operation date (a ride: when it finished) and created_at is when
    // the entry was recorded (a ride: when it was paid, often an hour or more
    // later). The recording time is shown only when the two are at least one
    // minute apart.
    showRecordedAt(entry) {
      var recorded = entry ? toCompanyMoment(entry.created_at) : null;
      if (!recorded) {
        return false;
      }
      var dated = toCompanyMoment(entry.journal_date);
      return !dated || Math.abs(recorded.diff(dated)) >= 60000;
    },
    performerName(entry) {
      return (entry && entry.created_by_name) || this.$t("finance.common.performedBySystem");
    },
    formatSource(value) {
      var map = {
        manual_journal: this.$t("finance.journals.manualJournals"),
        captain_booking_revenue: this.$t("finance.journals.bookingRevenue"),
        captain_settlement_paid: this.$t("finance.journals.settlementPaid"),
        captain_settlement_received: this.$t("finance.journals.settlementReceived"),
        captain_bonus_adjustment: this.$t("finance.journals.bonusAdjustments"),
        captain_penalty_adjustment: this.$t("finance.journals.penaltyAdjustments"),
        captain_hr_deduction_adjustment: this.$t("finance.journals.hrDeductions"),
        cash_movement: this.$t("finance.journals.cashMovements"),
        payroll_batch: this.$t("finance.journals.payrollBatches"),
        asset_entry: this.$t("finance.journals.assetEntries"),
        journal_reversal: this.$t("finance.journals.reversalSource"),
      };

      return map[value] || value;
    },
    formatEntryStatus(entry) {
      if (entry.is_reversed) {
        return this.$t("finance.journals.statusReversed");
      }

      if (entry.is_reversal) {
        return this.$t("finance.journals.statusReversal");
      }

      return this.$t("finance.journals.statusPosted");
    },
  },
};
</script>

<style scoped>
.entry-doer {
  color: #6b7a89;
}

.driver-account-row {
  margin-top: -8px;
}

.driver-account-none {
  color: #6b7a89;
  font-size: 13px;
}

.entity-leaf-chip {
  height: auto;
  white-space: normal;
}

.entry-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.entry-header {
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
}

.entry-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Final-version doc 2026-10-05: entry date with the recording time under it. */
.entry-date {
  display: flex;
  flex-direction: column;
}

.entry-recorded {
  color: #6b7a89;
  font-size: 12px;
  line-height: 1.3;
  cursor: help;
}

/* Isolated so that, after the Arabic label, the date keeps the same digit
   order as the entry date above it. */
.entry-recorded-value {
  unicode-bidi: isolate;
}

.entry-meta {
  font-weight: 600;
}

.summary-row {
  font-weight: 600;
}

.payroll-result-title {
  font-weight: 600;
  margin-bottom: 12px;
}

.journal-paging {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}

.journal-paging-total {
  color: #6b7a89;
}

.journal-paging-size {
  max-width: 120px;
}

.line-row {
  border: 1px solid #eceff1;
  border-radius: 8px;
  margin-bottom: 12px;
}
</style>